import { useMoviesStore } from "@multicines/stores";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { renderTimeNode } from "./Timer.helpers";
import Styles from "./Timer.styles";
import { TimerProps as Props, Timer as ITimer } from "./Timer.types";

const Timer: React.FC<Props> = props => {
  const { className, onFinish } = props;
  const [over, setOver] = useState(false);
  const reservationExpiration = useMoviesStore(
    store => store.reservationExpiration
  );
  const [timer, setTimer] = useState<ITimer>();
  const { minutes, seconds } = timer ?? {};
  const timerRef = useRef<ReturnType<typeof setInterval> | null>();

  const renderCompleteTime = useCallback(() => {
    if (typeof minutes !== "number" || typeof seconds !== "number") return;
    if (minutes === 0 && seconds === 0) return;
    return `${renderTimeNode(minutes, 1)}:${renderTimeNode(seconds)}`;
  }, [minutes, seconds]);

  const renderTime = useCallback(() => {
    if (typeof minutes !== "number" || typeof seconds !== "number") return;
    if (over) return;
    let timer: ITimer;

    if (minutes <= 0 && seconds <= 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      setOver(true);
      onFinish?.();
      return;
    } else if (seconds === 0) {
      timer = {
        minutes: minutes - 1,
        seconds: 59
      };
    } else {
      timer = {
        minutes,
        seconds: seconds - 1
      };
    }
    setTimer(timer);
  }, [onFinish, over, minutes, seconds]);

  useEffect(() => {
    timerRef.current = setInterval(renderTime, 1000);
    return () => {
      if (!timerRef.current) return;
      clearInterval(timerRef.current);
    };
  }, [renderTime]);

  useEffect(() => {
    if (!reservationExpiration) {
      if (timerRef.current) clearInterval(timerRef.current);
      setOver(true);
      return;
    }
    setOver(false);
    const now = dayjs();
    const isAfter = dayjs(reservationExpiration).isAfter(now);
    if (!isAfter) return;
    const end = dayjs(reservationExpiration);
    const minutes = end.diff(now, "minutes");
    const seconds = end.diff(now.clone().add(minutes, "minutes"), "seconds");
    setTimer({ minutes, seconds });
  }, [reservationExpiration]);

  return (
    <Styles className={`Timer ${className}`}>
      {timer ? (
        <p className="Timer__text">{`${renderCompleteTime()}`}</p>
      ) : null}
    </Styles>
  );
};

export default Timer;
